import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"
import {
	ArrowLeft,
	CalendarClock,
	History,
	Loader2,
	Pencil,
	RefreshCw,
	SlidersHorizontal,
	UserPlus,
	UserX,
} from "lucide-react"
import * as React from "react"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	Section,
	Stat,
	StatStrip,
} from "@/components/layout"
import { BACK_LINK_CLASS } from "@/components/promotions/back-link"
import {
	DetailColumn,
	DetailLayout,
	DetailPage,
	DetailPageHeader,
} from "@/components/promotions/detail-page"
import {
	describeStandardSellerFees,
	describeTranslatedName,
	formatBps,
	formatDateTime,
	formatWindow,
	TRANSLATED_NAME_LOCALES,
} from "@/components/promotions/promotion-helpers"
import { PromotionStatusBadge } from "@/components/promotions/promotion-status-badge"
import {
	CAMPAIGN_KIND_LABELS,
	PAUSE_HELP,
} from "@/components/seller-promotions/campaign-form.helpers"
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { AddSellersDialog } from "@/features/seller-promotions/AddSellersDialog"
import { CampaignEnrollmentsGrid } from "@/features/seller-promotions/CampaignEnrollmentsGrid"
import { describeGeography } from "@/features/seller-promotions/campaign-labels"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import { createTabSearch } from "@/hooks/use-url-tab"
import {
	useEndAllSellerPromotionEnrollments,
	useRequestSellerPromotionSweep,
	useSellerPromotionCampaign,
	useSetSellerPromotionActive,
} from "@/queries/marketplace"
import type { AdminSellerPromotionCampaign } from "@/types/promotions.types"

const TABS = ["overview", "sellers"] as const
type CampaignTab = (typeof TABS)[number]

export const Route = createFileRoute("/seller-promotions/$campaignId/")({
	component: SellerPromotionDetailPage,
	validateSearch: createTabSearch(TABS),
})

function SettingsSection({
	campaign,
}: {
	campaign: AdminSellerPromotionCampaign
}) {
	const appLinked = campaign.kind === "integration_linked"

	return (
		<DetailPanel title="Settings" icon={SlidersHorizontal}>
			<DetailPanelSection>
				<KeyValueList>
					<KeyValueItem label="Who gets it">
						{CAMPAIGN_KIND_LABELS[campaign.kind]}
					</KeyValueItem>
					{appLinked && (
						<KeyValueItem label="App">
							{campaign.integrationApp?.name ?? "None"}
						</KeyValueItem>
					)}
					<KeyValueItem label="Seller fee">
						{formatBps(campaign.takeRateBps)}
					</KeyValueItem>
					<KeyValueItem label="CardNexus Shield">
						{campaign.shieldIncluded ? "Offered for free" : "Not included"}
					</KeyValueItem>
					{appLinked && (
						<>
							<KeyValueItem label="Days per seller">
								{campaign.sellerPromoDurationDays ?? "None"}
							</KeyValueItem>
							<KeyValueItem label="Sellers already connected at the start">
								{campaign.includeExistingConnections
									? "Included"
									: "Not included"}
							</KeyValueItem>
						</>
					)}
					{campaign.kind !== "selected_sellers" && (
						<KeyValueItem label="Where">
							{describeGeography(campaign)}
						</KeyValueItem>
					)}
				</KeyValueList>
			</DetailPanelSection>
		</DetailPanel>
	)
}

function CampaignSummary({
	campaign,
}: {
	campaign: AdminSellerPromotionCampaign
}) {
	return (
		<DetailColumn>
			<DetailPanel title="Schedule" icon={CalendarClock}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Starts">
							{formatDateTime(campaign.startsAt)}
						</KeyValueItem>
						<KeyValueItem label="Ends">
							{campaign.endsAt ? (
								formatDateTime(campaign.endsAt)
							) : (
								<span className="text-meta">No end date</span>
							)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
			<DetailPanel title="Record" icon={History}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Created">
							{format(new Date(campaign.createdAt), "PPP p")}
						</KeyValueItem>
						<KeyValueItem label="Last updated">
							{formatDistanceToNow(new Date(campaign.updatedAt), {
								addSuffix: true,
							})}
						</KeyValueItem>
						<KeyValueItem label="Created by">
							{campaign.createdByUsername ? (
								<span>@{campaign.createdByUsername}</span>
							) : (
								<span className="font-mono">
									{campaign.createdBy ?? "None"}
								</span>
							)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
		</DetailColumn>
	)
}

function EndAllDialog({
	campaign,
	open,
	onOpenChange,
}: {
	campaign: AdminSellerPromotionCampaign
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const endAllMutation = useEndAllSellerPromotionEnrollments()
	const [reason, setReason] = React.useState("")

	const handleOpenChange = (next: boolean) => {
		if (!next && endAllMutation.isPending) {
			return
		}

		if (!next) {
			setReason("")
		}
		onOpenChange(next)
	}

	const handleConfirm = () => {
		endAllMutation.mutate(
			{ campaignId: campaign.id, reason: reason.trim() },
			{
				onSuccess: () => {
					setReason("")
					onOpenChange(false)
				},
			},
		)
	}

	return (
		<AlertDialog open={open} onOpenChange={handleOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						End the promotion for every seller
					</AlertDialogTitle>
					<AlertDialogDescription>
						Every seller still in {campaign.name}, including those whose app is
						disconnected, goes back to the standard fee immediately. This cannot
						be undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<div className="space-y-2">
					<Label htmlFor="end-all-reason">Reason</Label>
					<Textarea
						id="end-all-reason"
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						placeholder="e.g. Wrong rate set when the promotion was created"
						maxLength={500}
						rows={3}
					/>
					<p className="text-xs text-muted-foreground">
						Kept with each seller's promotion history
					</p>
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={endAllMutation.isPending}>
						Cancel
					</AlertDialogCancel>
					<Button
						variant="destructive"
						onClick={handleConfirm}
						disabled={reason.trim().length === 0 || endAllMutation.isPending}
					>
						{endAllMutation.isPending && <Loader2 className="animate-spin" />}
						End the promotion now
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

function SellerPromotionDetailPage() {
	const { campaignId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const { campaign, dataUpdatedAt, isLoading, error } =
		useSellerPromotionCampaign(campaignId)
	const setActiveMutation = useSetSellerPromotionActive()
	const sweepMutation = useRequestSellerPromotionSweep()
	const { hasRole } = useAdminRoles()
	const canOperate = hasRole(AdminRole.MarketplaceFinance)
	const [addSellersOpen, setAddSellersOpen] = React.useState(false)
	const [endAllOpen, setEndAllOpen] = React.useState(false)

	if (isLoading) {
		return (
			<DetailPage>
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-[78px]" />
				<DetailLayout>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
					</div>
					<Skeleton className="h-64" />
				</DetailLayout>
			</DetailPage>
		)
	}

	if (error || !campaign) {
		return (
			<DetailPage className="items-start gap-3">
				<p className="text-bad">
					{error?.message || "The seller promotion could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/seller-promotions">
						<ArrowLeft />
						Back to seller promotions
					</Link>
				</Button>
			</DetailPage>
		)
	}

	const hasSellerList = campaign.kind !== "all_sellers"
	const activeTab: CampaignTab =
		search.tab === "sellers" && hasSellerList ? "sellers" : "overview"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/seller-promotions/$campaignId",
			params: { campaignId },
			search: tab === "sellers" ? { tab: "sellers" } : {},
			replace: true,
		})
	}

	const canAddSellers =
		campaign.kind === "selected_sellers" &&
		campaign.status !== "paused" &&
		campaign.status !== "ended"
	const canEndAll = hasSellerList && campaign.ongoing > 0
	const showSellerActions =
		canOperate &&
		(canAddSellers || campaign.kind === "integration_linked" || canEndAll)
	const endAllLabel = `End the promotion for all ${campaign.ongoing.toLocaleString()} seller${campaign.ongoing === 1 ? "" : "s"} now`

	return (
		<DetailPage>
			<DetailPageHeader
				back={
					<Link to="/seller-promotions" className={BACK_LINK_CLASS}>
						<ArrowLeft />
						Seller promotions
					</Link>
				}
				title={campaign.name}
				badges={
					<>
						<code className="font-mono text-sm break-all text-muted-foreground">
							{campaign.key}
						</code>
						<PromotionStatusBadge status={campaign.status} />
					</>
				}
				description={
					<span className="flex flex-col gap-0.5">
						<span>Sellers see: {campaign.publicNames.en}</span>
						{TRANSLATED_NAME_LOCALES.map((locale) => (
							<span key={locale}>
								{describeTranslatedName(campaign.publicNames, locale)}
							</span>
						))}
						<span className="tabular-nums">
							{formatWindow(campaign.startsAt, campaign.endsAt)}
						</span>
					</span>
				}
				actions={
					<>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="flex items-center gap-2 pr-2">
									<Label htmlFor="campaign-active-switch">Switched on</Label>
									<Switch
										id="campaign-active-switch"
										checked={campaign.isActive}
										disabled={!canOperate || setActiveMutation.isPending}
										onCheckedChange={(checked) =>
											setActiveMutation.mutate({
												id: campaign.id,
												isActive: checked,
											})
										}
									/>
								</div>
							</TooltipTrigger>
							<TooltipContent className="max-w-xs">
								{PAUSE_HELP[campaign.kind]}
							</TooltipContent>
						</Tooltip>
						{showSellerActions && canAddSellers && (
							<Button onClick={() => setAddSellersOpen(true)}>
								<UserPlus />
								Add sellers
							</Button>
						)}
						{showSellerActions && campaign.kind === "integration_linked" && (
							<Button
								variant="outline"
								disabled={sweepMutation.isPending}
								onClick={() => sweepMutation.mutate()}
							>
								{sweepMutation.isPending ? (
									<Loader2 className="animate-spin" />
								) : (
									<RefreshCw />
								)}
								Check for new sellers now
							</Button>
						)}
						{showSellerActions && canEndAll && (
							<Button
								variant="destructiveOutline"
								className="h-auto min-h-[30px] max-w-full whitespace-normal"
								onClick={() => setEndAllOpen(true)}
							>
								<UserX />
								{endAllLabel}
							</Button>
						)}
						{canOperate && (
							<Button variant="outline" asChild>
								<Link
									to="/seller-promotions/$campaignId/edit"
									params={{ campaignId: campaign.id }}
								>
									<Pencil />
									Edit
								</Link>
							</Button>
						)}
					</>
				}
			/>

			<StatStrip>
				<Stat
					label="Seller fee"
					value={formatBps(campaign.takeRateBps)}
					detail={`Standard: ${describeStandardSellerFees()}`}
				/>
				{hasSellerList ? (
					<>
						<Stat
							label="Sellers in the promotion"
							value={campaign.sellersInPromotion.toLocaleString()}
							detail="Removed sellers not counted"
							muted={campaign.sellersInPromotion === 0}
						/>
						<Stat
							label="Paying the reduced fee now"
							value={campaign.liveNow.toLocaleString()}
							detail="Not suspended, not ended"
							muted={campaign.liveNow === 0}
						/>
					</>
				) : (
					<Stat
						label="Covers"
						value={describeGeography(campaign)}
						detail="Every seller shipping from there"
					/>
				)}
			</StatStrip>

			<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-6">
				<TabsList variant="line">
					<TabsTrigger value="overview">Overview</TabsTrigger>
					{hasSellerList && (
						<TabsTrigger value="sellers" className="gap-2">
							Sellers
							<span className="text-meta tabular-nums">
								{campaign.sellersInPromotion.toLocaleString()}
							</span>
						</TabsTrigger>
					)}
				</TabsList>

				<TabsContent value="overview" className="mt-0">
					<DetailLayout>
						<SettingsSection campaign={campaign} />
						<CampaignSummary campaign={campaign} />
					</DetailLayout>
				</TabsContent>

				{hasSellerList && (
					<TabsContent value="sellers" className="mt-0">
						<Section label="Sellers in this promotion">
							<CampaignEnrollmentsGrid
								campaign={campaign}
								canOperate={canOperate}
								refreshToken={dataUpdatedAt}
							/>
						</Section>
					</TabsContent>
				)}
			</Tabs>

			{campaign.kind === "selected_sellers" && (
				<AddSellersDialog
					campaignId={campaign.id}
					campaignName={campaign.name}
					open={addSellersOpen}
					onOpenChange={setAddSellersOpen}
				/>
			)}
			{hasSellerList && (
				<EndAllDialog
					campaign={campaign}
					open={endAllOpen}
					onOpenChange={setEndAllOpen}
				/>
			)}
		</DetailPage>
	)
}
